import {
  ONLINE_HUB_CATEGORY_ORDER,
  ONLINE_PATHWAY_ORDER,
  onlineCoursesHubContent,
  type OnlineHubCategory,
  type OnlineHubPathway,
} from "@/content/online-courses/hub";
import { CANONICAL_ORIGIN } from "@/utils/seo";
import { isOnlineBogofEligibleCourse } from "@/lib/online-course-bogof";

export type LocalOnlineCourseFaq = {
  question: string;
  answer: string;
};

export type LocalOnlineCourseProof = {
  quote: string;
  author: string;
};

export type LocalOnlineCourseImage = {
  src: string;
  alt: string;
};

export type LocalOnlineCourseFrontmatter = {
  slug: string;
  url: string;
  title: string;
  subtitle?: string;
  meta_title?: string;
  meta_description?: string;
  meta_image?: string;
  qualification_type: "cpd" | "vtct";
  course_type: "single-course" | "bundle";
  hub_category: OnlineHubCategory;
  categories: string[];
  audience: string[];
  audience_summary: string;
  prerequisites: string[];
  not_for: string[];
  one_line_outcome: string;
  access_summary: string;
  certificate_summary: string;
  support_summary: string;
  purchase_flow: "checkout" | "partner";
  price_pence: number;
  was_price_pence?: number | null;
  price_id?: string;
  payment_link_id?: string;
  partner_link?: string;
  partner_code?: string;
  partner_heading?: string;
  partner_text?: string;
  active: boolean;
  featured: boolean;
  package: boolean;
  best_seller: boolean;
  express_checkout_enabled: boolean;
  options_managed?: boolean;
  requires_examination: boolean;
  cpd_accredited: boolean;
  vtct_accredited: boolean;
  badges: string[];
  key_outcomes: string[];
  included: string[];
  faqs: LocalOnlineCourseFaq[];
  proof?: LocalOnlineCourseProof | null;
  related_courses: string[];
  fallback_cta_href: string;
  fallback_cta_label: string;
  hub_order: number;
  images: LocalOnlineCourseImage[];
};

export type LocalOnlineCourse = LocalOnlineCourseFrontmatter & {
  body: string;
};

export type LocalOnlineCourseListItem = Pick<
  LocalOnlineCourseFrontmatter,
  | "slug"
  | "url"
  | "title"
  | "meta_title"
  | "meta_description"
  | "meta_image"
  | "qualification_type"
  | "course_type"
  | "hub_category"
  | "categories"
  | "audience_summary"
  | "one_line_outcome"
  | "access_summary"
  | "purchase_flow"
  | "price_pence"
  | "was_price_pence"
  | "price_id"
  | "payment_link_id"
  | "partner_link"
  | "partner_code"
  | "partner_heading"
  | "partner_text"
  | "active"
  | "package"
  | "badges"
  | "key_outcomes"
  | "images"
  | "options_managed"
> & {
  admin_option_count?: number;
  admin_from_price_pence?: number;
  admin_max_price_pence?: number;
};

export const courseUsesManagedOptions = (
  course: Pick<
    LocalOnlineCourseListItem,
    "options_managed" | "admin_option_count"
  >,
) =>
  Boolean(course.options_managed) || (course.admin_option_count ?? 0) > 0;

export const enrichLocalOnlineCourseListItems = <
  T extends {
    slug: string;
    options_managed?: boolean;
    price_pence: number;
  },
>(
  courses: T[],
  adminOptionMap: Map<
    string,
    { count: number; minPricePence: number; maxPricePence: number }
  >,
): (T & {
  options_managed: boolean;
  admin_option_count: number;
  admin_from_price_pence?: number;
  admin_max_price_pence?: number;
})[] =>
  courses.map((course) => {
    const admin = adminOptionMap.get(course.slug);
    const hasAdmin = Boolean(admin && admin.count > 0);

    return {
      ...course,
      options_managed: Boolean(course.options_managed) || hasAdmin,
      admin_option_count: admin?.count ?? 0,
      ...(hasAdmin
        ? {
            admin_from_price_pence: admin!.minPricePence,
            admin_max_price_pence: admin!.maxPricePence,
          }
        : {}),
    };
  });

export const getHubDisplayPricePence = (
  course: Pick<
    LocalOnlineCourseListItem,
    "price_pence" | "admin_from_price_pence" | "admin_option_count"
  >,
) => {
  if ((course.admin_option_count ?? 0) > 0 && course.admin_from_price_pence != null) {
    return course.admin_from_price_pence;
  }

  return course.price_pence;
};

export const getHubPricePrefix = (
  course: Pick<
    LocalOnlineCourseListItem,
    "options_managed" | "admin_option_count"
  >,
) => (courseUsesManagedOptions(course) ? "From " : "");

type HubCourseLike = Pick<LocalOnlineCourseListItem, "slug" | "hub_category" | "title">;

export const toLocalOnlineCourseListItem = (
  course: LocalOnlineCourse
): LocalOnlineCourseListItem => ({
  slug: course.slug,
  url: course.url,
  title: course.title,
  meta_title: course.meta_title,
  meta_description: course.meta_description,
  meta_image: course.meta_image,
  qualification_type: course.qualification_type,
  course_type: course.course_type,
  hub_category: course.hub_category,
  categories: course.categories,
  audience_summary: course.audience_summary,
  one_line_outcome: course.one_line_outcome,
  access_summary: course.access_summary,
  purchase_flow: course.purchase_flow,
  price_pence: course.price_pence,
  was_price_pence: course.was_price_pence,
  price_id: course.price_id,
  payment_link_id: course.payment_link_id,
  partner_link: course.partner_link,
  partner_code: course.partner_code,
  partner_heading: course.partner_heading,
  partner_text: course.partner_text,
  active: course.active,
  package: course.package,
  badges: course.badges,
  key_outcomes: course.key_outcomes,
  images: course.images,
  options_managed: course.options_managed ?? false,
});

export const getFeaturedLocalOnlineCourses = <T extends HubCourseLike>(
  courses: T[]
): T[] => {
  const featuredSet = new Set<string>(onlineCoursesHubContent.featuredSlugs);
  return courses.filter((course) => featuredSet.has(course.slug));
};

export const getBestSellerLocalOnlineCourses = <T extends HubCourseLike>(
  courses: T[]
): T[] => {
  const bestSellerSet = new Set<string>(onlineCoursesHubContent.bestSellerSlugs);
  return courses.filter((course) => bestSellerSet.has(course.slug));
};

export const groupLocalOnlineCourses = <T extends HubCourseLike>(courses: T[]) => {
  return ONLINE_HUB_CATEGORY_ORDER.map((category) => ({
    category,
    courses: courses.filter((course) => course.hub_category === category),
  }));
};

export const getLocalOnlineCourseUrl = (slug: string) =>
  `${CANONICAL_ORIGIN}/courses/elearning/${slug}/`;

export const adaptLocalOnlineCourseToLegacyEntry = (
  course: LocalOnlineCourse | LocalOnlineCourseListItem,
) => {
  const primaryImage =
    course.images[0]?.src || course.meta_image || `${CANONICAL_ORIGIN}/logo/cosmetic-college-logo.png`;

  return {
    sys: {
      id: course.slug,
    },
    fields: {
      title: course.title,
      type: "Online Course",
      url: course.url,
      package: course.package,
      categories: course.categories.length > 0 ? course.categories : ["online"],
      listingImage: {
        fields: {
          title: course.images[0]?.alt || course.title,
          file: {
            url: primaryImage.replace("https:", ""),
          },
        },
      },
      metadata: {
        fields: {
          title: course.meta_title || course.title,
          description: course.meta_description || course.one_line_outcome,
          image: {
            fields: {
              file: {
                url: primaryImage.replace("https:", ""),
              },
            },
          },
        },
      },
      optionsManaged: course.options_managed ?? false,
      configurationId: null,
      optionCode: null,
      bogofEligible: isOnlineBogofEligibleCourse(course),
      data: {
        full: (course.price_pence / 100).toFixed(2),
        was: course.was_price_pence || null,
        active: course.active,
        priceId: course.price_id || null,
        paymentLinkId: course.payment_link_id || null,
        partner: course.purchase_flow === "partner",
        partnerLink: course.partner_link || null,
        partnerCode: course.partner_code || null,
        heading: course.partner_heading || null,
        text: course.partner_text || null,
        price: (course.price_pence / 100).toFixed(2),
      },
      pricing: course.price_id
        ? {
            id: course.price_id,
            unit_amount: course.price_pence,
          }
        : null,
    },
  };
};

export const getRelatedLocalOnlineCourses = (
  course: LocalOnlineCourse,
  allCourses: LocalOnlineCourse[]
) => {
  const relatedSet = new Set(course.related_courses);
  const explicit = allCourses.filter((item) => relatedSet.has(item.slug));

  if (explicit.length >= 3) {
    return explicit.slice(0, 3);
  }

  const fallback = allCourses.filter(
    (item) =>
      item.slug !== course.slug &&
      item.hub_category === course.hub_category &&
      !relatedSet.has(item.slug)
  );

  return [...explicit, ...fallback].slice(0, 3);
};

export const getOnlineHubCategoryContent = (category: OnlineHubCategory) =>
  onlineCoursesHubContent.categoryCards.find((card) => card.id === category);

export const getOnlineHubCategoryPath = (category: OnlineHubCategory) =>
  `/courses/elearning/${category}/`;

export const getOnlineHubPathwayPath = (pathway: OnlineHubPathway) =>
  `/courses/elearning/pathways/${pathway.replace(/_/g, "-")}/`;

export const isOnlineHubCategory = (
  value: string,
): value is OnlineHubCategory =>
  ONLINE_HUB_CATEGORY_ORDER.includes(value as OnlineHubCategory);

export const isOnlineHubPathway = (value: string): value is OnlineHubPathway =>
  ONLINE_PATHWAY_ORDER.includes(value as OnlineHubPathway);

export const parseOnlineHubPathwayParam = (
  value: string,
): OnlineHubPathway | null => {
  const normalized = value.replace(/-/g, "_");
  return isOnlineHubPathway(normalized) ? normalized : null;
};

export const getOnlineHubPathwayContent = (pathway: OnlineHubPathway) =>
  onlineCoursesHubContent.trainingTypes.types.find((type) => type.id === pathway);

export const getLocalOnlineCoursesByHubCategory = <T extends HubCourseLike>(
  category: OnlineHubCategory,
  courses: T[],
) => courses.filter((course) => course.hub_category === category);
